/* acrylic-topper.js — personalised acrylic name/message topper upsell.
 *
 * Renders a live SVG preview of whatever the shopper types, in Latin or Arabic, and presents
 * itself to cart.js as an ordinary [data-addon] tile so the existing add-on purchase path
 * (batched items[] add, _gift/_addon_of parent token, double-submit guard) carries it without
 * knowing it exists. See snippets/acrylic-topper.liquid for why.
 *
 * Three rules this file is built around:
 *   1. No network, no debounce, no timeout on the preview. The `input` event redraws it.
 *   2. Never innerHTML with shopper text — textContent only.
 *   3. Never block the product page. Every entry point is wrapped; a failure costs the upsell
 *      and nothing else.
 */
(function () {
  'use strict';

  var VERSION = 'v1';

  /* Arabic, Arabic Supplement/Extended-A and the presentation-forms blocks. Deliberately a
     SCRIPT test, not a language test: it picks the font and the text direction, nothing more.
     Written as code-point ranges rather than a regex character class so this source stays pure
     ASCII. A literal class of exotic characters here is one stray byte away from an
     unterminated regex, and that takes the whole file down with it. */
  function hasArabic(s) {
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if ((c >= 0x0600 && c <= 0x06FF) || (c >= 0x0750 && c <= 0x077F) ||
          (c >= 0x08A0 && c <= 0x08FF) || (c >= 0xFB50 && c <= 0xFDFF) ||
          (c >= 0xFE70 && c <= 0xFEFF)) return true;
    }
    return false;
  }

  /* Rejected outright: control characters, line breaks, and anything pictographic. The Arabic
     joiners U+200C/U+200D are deliberately NOT rejected - Arabic shaping depends on them. */
  function hasBadChar(s) {
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c <= 0x1F || c === 0x7F) return true;        // controls, tab, newline
      if (c === 0x2028 || c === 0x2029) return true;   // line / paragraph separators
      if (c >= 0xD800 && c <= 0xDBFF) return true;     // astral plane: emoji and pictographs
      if (c === 0xFE0F) return true;                   // emoji variation selector
      if (c >= 0x231A && c <= 0x23FA) return true;     // watches, media symbols
      if (c >= 0x2600 && c <= 0x27BF) return true;     // misc symbols, dingbats
      if (c >= 0x2B00 && c <= 0x2BFF) return true;     // arrows and symbols
    }
    return false;
  }

  /* A connected SCRIPT face, because that is what an acrylic name topper is. The theme heading
     font is an upright sans and read as a shop sign rather than a topper. These are all system
     faces - nothing is downloaded, nothing is unlicensed - which does mean the exact shape
     varies by platform. Set the production face in the "Latin topper font" setting to pin it. */
  var FONT_LATIN = "'Affy Topper Script', 'Snell Roundhand', 'Brush Script MT', 'Segoe Script', cursive";
  /* System Arabic faces only — nothing is downloaded here. A production-approved, laser-safe
     Arabic face is supplied through the section setting; see docs/shopify/ACRYLIC_TOPPER.md. */
  var FONT_ARABIC = "var(--font-arabic), 'Noto Naskh Arabic', 'Geeza Pro', Tahoma, sans-serif";

  /* Restrained, per §7 — a metallic ramp and one highlight, not a chrome effect. `edge` gives
     the acrylic its cut outline and is what keeps Black legible over a dark bouquet (§55). */
  var MATERIALS = {
    gold:    { stops: ['#f6e4ae', '#e0bd68', '#c89a37', '#e6cb84', '#b6862b'], edge: 'rgba(130,92,22,.45)',  shadow: 'rgba(90,60,0,.3)' },
    silver:  { stops: ['#ffffff', '#d4dade', '#939ba3', '#f2f5f7', '#7d858d'], edge: 'rgba(90,98,106,.5)',   shadow: 'rgba(40,50,60,.34)' },
    black:   { stops: ['#6a6a6a', '#2e2e2e', '#131313', '#4a4a4a', '#070707'], edge: 'rgba(255,255,255,.45)', shadow: 'rgba(0,0,0,.45)' },
    rose:    { stops: ['#fde4dc', '#e9ab9b', '#b9707c', '#f6cfc6', '#9c5964'], edge: 'rgba(140,72,80,.5)',   shadow: 'rgba(120,60,60,.34)' },
    white:   { stops: ['#ffffff', '#f4f4f4', '#dcdcdc', '#ffffff', '#cfcfcf'], edge: 'rgba(120,120,120,.55)', shadow: 'rgba(0,0,0,.26)' },
    clear:   { stops: ['#ffffff', '#eef6fa', '#d3e2ea', '#ffffff', '#c2d4de'], edge: 'rgba(120,150,165,.6)',  shadow: 'rgba(40,70,90,.3)' },
    default: { stops: ['#ffffff', '#d4dade', '#939ba3', '#f2f5f7', '#7d858d'], edge: 'rgba(90,98,106,.5)',   shadow: 'rgba(40,50,60,.34)' }
  };
  /* Geometry of the preview, in viewBox units. SAFE_W/SAFE_H are the manufacturable area the
     text must fit inside; MIN_SIZE is the point below which we stop shrinking and show a
     validation message instead of rendering something unreadably small (§63). */
  var VB_W = 420, VB_H = 300;
  var SAFE_W = 366, SAFE_H = 150;
  var PREF_SIZE = 78, MIN_SIZE = 22;
  var TEXT_CY = 96;
  /* The limit is a MESSAGE limit, not a name limit, so the text has to wrap. At 50 characters a
     single line would need roughly a 15px face to fit 366 units — unreadable, and it would put
     the card into a permanent "too long" state. Three lines is what a real acrylic message
     topper looks like, and it is where legibility stops being a problem. */
  var MAX_LINES = 3;
  /* Vertical metrics of the script face, as fractions of the type size. Line spacing is derived
     from these PER PAIR OF LINES rather than fixed, because how two rows meet depends entirely
     on which letters they contain:
       - a row of x-height letters only (a, s, o) tops out at XH_RISE, so the row above must sit
         close for them to touch at all;
       - a row containing h, k, l, d or a capital reaches ASC_RISE, far higher, so the row above
         has to sit further away or the two collide;
       - a row above ending in g, j, p, q or y hangs down by DESC_DROP, which closes part of the
         gap on its own.
     Solving for those three gives a spacing where the rows always overlap by LINE_OVERLAP - so
     they are fused by their own letterforms and no connecting tab is ever needed. A fixed line
     height cannot do this: whatever value you pick is too tight for one case and too loose for
     the other, which is why a visible joint kept appearing. */
  var ASC_RISE = 0.72, XH_RISE = 0.50, DESC_DROP = 0.22, LINE_OVERLAP = 0.07, MIN_ADVANCE = 0.38;
  /* Arabic has no ascender/descender distinction to test for: alef and lam run tall on almost
     every word, and yeh, jeem and ain hang deep below the baseline. Asking the Latin helpers
     about it returns false for both, which handed it x-height spacing and stacked the rows
     straight through each other. It gets its own, roomier pair. */
  var AR_RISE = 0.80, AR_DROP = 0.30;
  /* An arbitrary measuring size. Text width scales linearly with font-size, so measuring once
     at REF_SIZE tells us the size any target width needs - no iteration. */
  var REF_SIZE = 100;
  /* Stake geometry. OVERLAP is what makes stake and lettering read as one cut piece. */
  /* STEM_BITE: how far ABOVE the last baseline the stick starts, as a fraction of the type
     size. It must bite into the glyphs or the stick reads as a separate piece. */
  /* STEM_BITE: how far ABOVE the last baseline the stick begins, as a fraction of type size.
     It only has to tuck UNDER a letter. At 0.45 it started near the cap height - so on one short
     word it speared the letter, and with lines overlapping at LINE_H it reached past the last
     line into the one above and crossed the whole piece. A shallow bite hides it behind the
     glyph it joins. Length is derived from the text block, not fixed: a fixed 132 next to a
     single letter is a pole with a bead on it. */
  var STEM_W = 3.4, STEM_BITE = 0.02, STEM_LEN_MIN = 0.95, STEM_LEN_RATIO = 0.85, STEM_TIP = 0.17;
  /* One-piece geometry, all as fractions of the font size so they stay proportional.
     JOIN_W - stroke that closes the hairline between neighbouring letters
     TAB_W  - half-width of the vertical tab bridging one line to the next */
  /* BRIDGE_Y: how far above the baseline the word-to-word tab sits; BRIDGE_H its
     thickness. Low in the letterforms, so it reads as a connection rather than a rule. */
  var JOIN_W = 0.022, TAB_W = 0.032, BRIDGE_Y = 0.13, BRIDGE_H = 0.07;

  var ASCENDERS = 'bdfhklt';
  var DESCENDERS = 'gjpqy';
  function hasTall(line) {
    for (var i = 0; i < line.length; i++) {
      var c = line.charAt(i);
      if (ASCENDERS.indexOf(c) > -1) return true;
      if (c >= 'A' && c <= 'Z') return true;
      if (c >= '0' && c <= '9') return true;
    }
    return false;
  }
  function hasDrop(line) {
    for (var i = 0; i < line.length; i++) if (DESCENDERS.indexOf(line.charAt(i)) > -1) return true;
    return false;
  }
  /* Baseline-to-baseline distance for one pair of rows, in fractions of the type size. */
  function advanceRatio(upper, lower) {
    if (hasArabic(upper) || hasArabic(lower)) {
      return AR_RISE + AR_DROP - LINE_OVERLAP;
    }
    var rise = hasTall(lower) ? ASC_RISE : XH_RISE;
    var drop = hasDrop(upper) ? DESC_DROP : 0;
    return Math.max(MIN_ADVANCE, rise + drop - LINE_OVERLAP);
  }
  /* Cumulative baseline offsets, and the block's total height, both in size fractions. */
  function lineOffsets(lines) {
    var out = [0], total = 0;
    for (var i = 0; i + 1 < lines.length; i++) {
      total += advanceRatio(lines[i], lines[i + 1]);
      out.push(total);
    }
    return { offsets: out, height: total + 1 };
  }

  var uid = 0;

  /* Liquid's `t` filter HTML-escapes, and JSON-encoding preserves the entity, so "Add {{ name }}'s
     topper" arrives as "Add {{ name }}&#39;s topper". textContent does NOT decode entities, so the
     button would literally read "&#39;". These are our own locale strings, never shopper input,
     so decoding them is safe — and it is only ever applied to cfg.copy, never to typed text. */
  var ENTITIES = {
    '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"',
    '&#39;': "'", '&#x27;': "'", '&#x2F;': '/', '&nbsp;': ' '
  };
  function decodeEntities(s) {
    return String(s == null ? '' : s)
      .replace(/&(?:amp|lt|gt|quot|nbsp|#39|#x27|#x2F);/g, function (m) { return ENTITIES[m] || m; });
  }

  function parseConfig(root) {
    var el = root.querySelector('script[type="application/json"][data-topper-config]');
    if (!el) return null;
    try {
      var cfg = JSON.parse(el.textContent);
      if (cfg && cfg.copy) {
        for (var k in cfg.copy) {
          if (Object.prototype.hasOwnProperty.call(cfg.copy, k)) cfg.copy[k] = decodeEntities(cfg.copy[k]);
        }
      }
      return cfg;
    } catch (e) { return null; }
  }

  function scriptOf(s) { return hasArabic(s) ? 'arabic' : 'latin'; }

  function emit(name, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, detail || {}));
    } catch (e) { /* analytics must never break commerce */ }
  }

  function build(root, cfg) {
    var id = 'tpr' + (++uid);
    var stage = root.querySelector('[data-topper-svg]');
    var input = root.querySelector('[data-topper-input]');
    var listEl   = root.querySelector('[data-topper-list]');
    var chosenEl = root.querySelector('[data-topper-chosen]');
    var addBtn   = root.querySelector('[data-topper-add]');
    var countEl  = root.querySelector('[data-topper-count]');
    var sumEl    = root.querySelector('[data-topper-sum]');
    var matsBox = root.querySelector('[data-topper-mats]');
    var errBox = root.querySelector('[data-topper-error]');
    var eyebrow = root.querySelector('[data-topper-eyebrow]');
    var blurb = root.querySelector('[data-topper-blurb]');
    var priceEl = root.querySelector('[data-topper-price]');
    var matLabelEl = root.querySelector('[data-topper-matlabel]');
    var addPriceEl = root.querySelector('[data-topper-addprice]');
    var addLabelEl = root.querySelector('[data-topper-addlabel]');
    var addTickEl  = root.querySelector('[data-topper-addtick]');
    var liveEl     = root.querySelector('[data-topper-live]');
    var addLabelDefault = addLabelEl ? addLabelEl.textContent : '';
    var freeEl     = root.querySelector('[data-topper-free]');
    /* Affy's product form is #ProductForm, and its Add to cart lives OUTSIDE the form, joined by
       form="ProductForm" - which is why everything below works off form.elements rather than a
       hand-written list of buttons. */
    var form = document.getElementById('ProductForm') || (root.closest && root.closest('form[action*="/cart/add"]'));
    if (!stage || !input || !listEl || !addBtn) return null;

    var mats = (cfg.materials || []).filter(function (m) { return m && m.variantId; });
    if (!mats.length) return null;

    /* Default material: the merchant's choice if it is actually sellable, else the first one
       that is. Previewing a variant that cannot be bought would be a dead end at the checkbox. */
    var sellable = mats.filter(function (m) { return m.available; });
    if (!sellable.length) return null;
    var wanted = String(cfg.defaultMaterial || '').toLowerCase();
    var current = sellable.filter(function (m) {
      return m.key === wanted || String(m.label).toLowerCase() === wanted;
    })[0] || sellable[0];

    /* Two halves. The BUILDER is what the field, the chips and the preview describe - one topper
       being composed, bought by nothing until Add is pressed. `items` is what has actually been
       added, and each entry becomes its own cart line, which is why two different names can be
       ordered at once. Nothing is `selected`: the list is the selection. */
    /* ONE list, whatever put it there. A ready-made design and a typed name are the same kind of
       thing - words, a material, a count - and they were two kinds of state until a design could
       be ordered in two colours at once. Then the tile could no longer hold it: a tile is one
       design in one place, and "one gold and one silver" is two rows. So the list holds
       everything, the list emits every cart line, and a tile is a control that reads and writes
       the row for (its design, the colour chosen right now). It cannot misrepresent an order
       because it never claims to be one. */
    var state = {
      items: [],
      seq: 0,
      text: '',
      script: 'latin',
      material: current,
      valid: true,
      error: ''
    };

    /* ---- SVG shell. Static markup only: the shopper's text is set with textContent below. ---- */
    stage.innerHTML =
      '<svg viewBox="0 0 ' + VB_W + ' ' + VB_H + '" role="img" preserveAspectRatio="xMidYMid meet" focusable="false">' +
        '<defs>' +
          '<linearGradient id="' + id + 'g" x1="0" y1="0" x2="0" y2="1">' +
            '<stop offset="0%" class="s0"/><stop offset="30%" class="s1"/><stop offset="52%" class="s2"/>' + '<stop offset="64%" class="s3"/><stop offset="100%" class="s4"/>' +
          '</linearGradient>' +
          '<filter id="' + id + 'f" x="-20%" y="-20%" width="140%" height="140%">' +
            '<feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-opacity=".45"/>' +
          '</filter>' +
        '</defs>' +
        '<g filter="url(#' + id + 'f)">' +
          '<path class="tpr-stem" d="M0 0"/>' +
          '<path class="tpr-join" d="M0 0"/>' +
          '<text class="tpr-text" x="' + (VB_W / 2) + '" y="' + TEXT_CY + '" text-anchor="middle"></text>' +
          /* Off-canvas twin used only to measure candidate lines while wrapping, so measuring
             never disturbs what is on screen. Same font settings are copied onto it. */
        '</g>' +
        '<text class="tpr-measure" x="0" y="-999" visibility="hidden" aria-hidden="true"></text>' +
      '</svg>';

    var svg = stage.querySelector('svg');
    var textEl = stage.querySelector('.tpr-text');
    var measEl = stage.querySelector('.tpr-measure');
    var stemEl = stage.querySelector('.tpr-stem');
    var joinEl = stage.querySelector('.tpr-join');
    var stops = ['.s0', '.s1', '.s2', '.s3', '.s4'].map(function (c) { return stage.querySelector(c); });
    var shapes = [stemEl, joinEl];
    svg.setAttribute('aria-label', (cfg.copy && cfg.copy.placeholder) || '');
    textEl.setAttribute('fill', 'url(#' + id + 'g)');
    shapes.forEach(function (s) { if (s) s.setAttribute('fill', 'url(#' + id + 'g)'); });

    /* ---- Material chips ---- */
    var chips = [];
    if (matsBox && cfg.showMaterials !== false) {
      mats.forEach(function (m) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'topper__mat';
        b.setAttribute('role', 'radio');
        b.setAttribute('data-mat', m.key);
        b.disabled = !m.available;
        var sw = document.createElement('span');
        sw.className = 'topper__swatch';
        var def = MATERIALS[m.key] || MATERIALS.default;
        sw.style.background = 'linear-gradient(160deg,' + def.stops[0] + ',' + def.stops[1] + ' 55%,' + def.stops[2] + ')';
        var lb = document.createElement('span');
        lb.className = 'topper__matlabel';
        lb.textContent = m.available ? m.label : m.label + ' — ' + ((cfg.copy && cfg.copy.soldOut) || '');
        b.appendChild(sw); b.appendChild(lb);
        b.addEventListener('click', function () {
          if (!m.available) return;
          /* §22: previewing a material is not consent to buy one. Selection state is untouched. */
          state.material = m;
          paint();
          renderPresets();   // the tiles follow the colour, and re-read their own row
          sync();
          emit('topper_material_selected', { material: m.key, product_id: cfg.productId });
        });
        matsBox.appendChild(b);
        chips.push({ el: b, m: m });
      });
    }

    /* ---- Line breaking + auto-fit ----
       Measure what the browser actually rendered; never guess from character count. Because the
       field accepts a short MESSAGE, not just a name, the text wraps across up to MAX_LINES and
       the font size is then chosen so the whole block fits the plate. */

    /* Width of one candidate line, measured on the hidden twin so the visible text is never
       disturbed mid-calculation. */
    function measure(str, size) {
      measEl.style.fontFamily = textEl.style.fontFamily;
      measEl.style.fontWeight = textEl.style.fontWeight || '';
      measEl.style.fontStyle = textEl.style.fontStyle || '';
      measEl.style.letterSpacing = textEl.style.letterSpacing || '';
      measEl.style.direction = textEl.style.direction;
      measEl.style.unicodeBidi = textEl.style.unicodeBidi;
      measEl.setAttribute('font-size', String(size));
      measEl.textContent = str;
      try { return measEl.getComputedTextLength(); } catch (e) { return 0; }
    }

    /* Split into EXACTLY n lines, choosing the break points that MINIMISE THE WIDEST LINE.
       The earlier greedy pass broke as soon as a line passed its share, which put "Get" alone
       above "Well Soon"; the merchant's own product sets it as "Get Well" / "Soon". Trying every
       break and keeping the most balanced one gets that right.

       Cost is bounded: candidate line widths are memoised, and above MAX_EXACT words it falls
       back to the greedy pass rather than exploring a combinatorial number of splits on every
       keystroke. Real topper text is a handful of words, so the exact path is what runs. */
    var MAX_EXACT = 9;

    function splitInto(words, n, totalW) {
      if (n <= 1 || words.length <= 1) return [words.join(' ')];
      if (n >= words.length) return words.slice();

      var memo = {};
      function widthOf(str) {
        if (!(str in memo)) memo[str] = measure(str, REF_SIZE);
        return memo[str];
      }

      if (words.length <= MAX_EXACT) {
        var best = null;
        var choose = function (from, breaks) {
          if (breaks.length === n - 1) {
            var cuts = [0].concat(breaks, [words.length]);
            var widest = 0, ls = [];
            for (var i = 0; i < n; i++) {
              var ln = words.slice(cuts[i], cuts[i + 1]).join(' ');
              ls.push(ln);
              var w = widthOf(ln);
              if (w > widest) widest = w;
            }
            if (!best || widest < best.w) best = { w: widest, lines: ls };
            return;
          }
          var limit = words.length - (n - breaks.length - 2);
          for (var p = from; p < limit; p++) choose(p + 1, breaks.concat([p]));
        };
        choose(1, []);
        if (best) return best.lines;
      }

      /* Greedy fallback for unusually wordy input. */
      var target = totalW / n, lines = [], cur = [], curW = 0;
      for (var j = 0; j < words.length; j++) {
        var left = n - lines.length;
        if (cur.length && (words.length - j) < left) { lines.push(cur.join(' ')); cur = []; curW = 0; }
        var ww = widthOf(words[j]);
        if (cur.length && curW + ww > target * 1.06 && lines.length < n - 1) {
          lines.push(cur.join(' ')); cur = [words[j]]; curW = ww;
        } else { cur.push(words[j]); curW += ww; }
      }
      if (cur.length) lines.push(cur.join(' '));
      return lines;
    }

    /* One tspan per line, centred, with the stem hung off the bottom of the block. textContent
       per tspan, never innerHTML. Shared by both the single-line and the wrapped paths. */
    /* Union of the real geometry. Never ask a container for this: anything hidden off-canvas
       inside it silently inflates the answer. */
    function unionBox(els) {
      var x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
      for (var i = 0; i < els.length; i++) {
        var b;
        try { b = els[i] && els[i].getBBox(); } catch (e) { continue; }
        if (!b || (!b.width && !b.height)) continue;
        if (b.x < x1) x1 = b.x;
        if (b.y < y1) y1 = b.y;
        if (b.x + b.width > x2) x2 = b.x + b.width;
        if (b.y + b.height > y2) y2 = b.y + b.height;
      }
      return isFinite(x1) ? { x: x1, y: y1, width: x2 - x1, height: y2 - y1 } : null;
    }

    /* x of the glyph nearest the line's centre. The stick has to emerge from a LETTER: parked
       at the exact geometric centre it lands in the gap between two letters as often as not,
       and then reads as a pole behind the text rather than part of it. Spaces are skipped so it
       never attaches to the gap between words. */
    function anchorX(line, size, cx) {
      var total = measure(line, size);
      if (!total) return cx;
      var left = cx - total / 2;
      var best = cx, bestDist = Infinity, prev = 0;
      for (var i = 0; i < line.length; i++) {
        var upto = measure(line.slice(0, i + 1), size);
        if (line.charAt(i) !== ' ') {
          var mid = left + (prev + upto) / 2;
          var d = Math.abs(mid - cx);
          if (d < bestDist) { bestDist = d; best = mid; }
        }
        prev = upto;
      }
      return best;
    }

    /* Centre-x of every character in `line` that satisfies `pred`, in the same centred space
       anchorX() uses. */
    function charXs(line, size, cx, pred) {
      var total = measure(line, size);
      if (!total) return [];
      var left = cx - total / 2, out = [], prev = 0;
      for (var i = 0; i < line.length; i++) {
        var upto = measure(line.slice(0, i + 1), size);
        if (pred(line.charAt(i))) out.push(left + (prev + upto) / 2);
        prev = upto;
      }
      return out;
    }

    /* Where two lines should be tied together. On a real cut piece the join hides behind a tall
       letter - an ascender on the lower line reaching up into the line above, or a descender on
       the upper line dropping into it - so the finished piece shows no connector at all. Picking
       the geometric centre instead is what left a visible bar in the open space between words. */
    var ASCENDERS = 'bdfhklt';
    var DESCENDERS = 'gjpqy';
    function joinX(upper, lower, size, cx) {
      var isAsc = function (c) { return ASCENDERS.indexOf(c) > -1 || (c >= 'A' && c <= 'Z'); };
      var isDesc = function (c) { return DESCENDERS.indexOf(c) > -1; };
      var cands = charXs(lower, size, cx, isAsc).concat(charXs(upper, size, cx, isDesc));
      if (!cands.length) return null;
      var best = cands[0];
      for (var i = 1; i < cands.length; i++) {
        if (Math.abs(cands[i] - cx) < Math.abs(best - cx)) best = cands[i];
      }
      return best;
    }

    function render(lines, size, fits) {
      textEl.setAttribute('font-size', String(size));
      /* ONE PIECE, not type on a page. A laser cuts a single closed outline, so the preview has
         to show what will actually come off the bed: letters fused, no floating parts.

         The stroke does the fusing. Painted BEHIND the fill (paint-order) in the same gradient,
         it fattens every glyph by half its width on each side, which closes the hairline gaps a
         script face leaves between letters — the same allowance a cutter would add by hand.
         Round joins keep the swelling off the sharp corners of the script. */
      textEl.setAttribute('stroke', 'url(#' + id + 'g)');
      textEl.setAttribute('stroke-width', (size * JOIN_W).toFixed(2));
      textEl.setAttribute('stroke-linejoin', 'round');
      textEl.setAttribute('stroke-linecap', 'round');
      textEl.setAttribute('paint-order', 'stroke');
      textEl.textContent = '';
      /* Baselines are spaced per PAIR of rows, so the offsets are cumulative rather than a
         constant step. */
      var lo = lineOffsets(lines);
      var top0 = TEXT_CY - (lo.offsets[lo.offsets.length - 1] * size) / 2;
      textEl.setAttribute('y', String(top0));
      for (var j = 0; j < lines.length; j++) {
        var ts = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        ts.setAttribute('x', String(VB_W / 2));
        ts.setAttribute('dy', j === 0 ? '0' : String((lo.offsets[j] - lo.offsets[j - 1]) * size));
        ts.textContent = lines[j];
        textEl.appendChild(ts);
      }

      /* EVERY gap gets a joint, because the whole thing has to lift off the bed as one piece.
         A connected script fuses its own letters (that is what JOIN_W does), but two things it
         cannot fuse: the SPACE between words on a line, and the gap between LINES. Both would
         drop out of the sheet as separate parts. So a maker adds a small tab at each - and so
         do we, low in the letterforms where it reads as part of the design. */
      var joins = '';
      var half = VB_W / 2;

      // --- word-to-word, within a line ---
      for (var li = 0; li < lines.length; li++) {
        var text = lines[li];
        if (text.indexOf(' ') < 0) continue;
        var lineW = measure(text, size);
        var left = half - lineW / 2;
        var baseY = top0 + lo.offsets[li] * size;
        for (var ci = 0; ci < text.length; ci++) {
          if (text.charAt(ci) !== ' ') continue;
          /* Measure the tail from the RIGHT rather than measuring a string that ends in a
             space: trailing spaces are dropped by getComputedTextLength, which would collapse
             the bridge to zero width exactly where it is needed. */
          var xEnd = left + measure(text.slice(0, ci), size);
          var xStart = left + lineW - measure(text.slice(ci + 1), size);
          if (xStart <= xEnd) continue;
          var over = size * 0.05;                 // bite into the glyph on each side
          var yTop = baseY - size * BRIDGE_Y;
          var yBot = yTop + size * BRIDGE_H;
          joins += 'M' + (xEnd - over) + ' ' + yTop + ' L' + (xStart + over) + ' ' + yTop +
                   ' L' + (xStart + over) + ' ' + yBot + ' L' + (xEnd - over) + ' ' + yBot + ' Z ';
        }
      }

      /* No line-to-line tab. Spacing is solved so consecutive rows OVERLAP by LINE_OVERLAP,
         whatever letters they contain, so they are fused by their own letterforms - which is
         what the reference pieces do and why they show no connector anywhere. */
      joinEl.setAttribute('d', joins);
      /* The stick grows out of the LAST baseline joint rather than floating under the bbox,
         so the outline is continuous from the top of the lettering to the tip. */
      var lastBaseline = top0 + lo.offsets[lo.offsets.length - 1] * size;
      var blockH = lo.height * size;
      /* Centre of the INK, not of the advance width. text-anchor:middle centres on the advance,
         which is not where the letters visually sit, and the viewBox is then refitted to the ink -
         so anchoring the stick to VB_W/2 left it measurably off centre (-3 on "Mariam", -6 on
         "Happy Birthday").

         No inset, and no snapping to a glyph. Adjacent extents in a connected script TOUCH, so
         there are no real gaps to avoid - the inset invented them and pushed the stick off to one
         side, which is exactly what it was added to prevent. The baseline runs continuously
         across the word, so the ink centre is always on ink. */
      var inkBox = textEl.getBBox();
      var cx = inkBox.width ? inkBox.x + inkBox.width / 2 : VB_W / 2;
      var top = lastBaseline - size * STEM_BITE;
      var len = Math.max(size * STEM_LEN_MIN, blockH * STEM_LEN_RATIO);
      var w = STEM_W, shaft = top + len, tip = shaft + size * STEM_TIP;
      stemEl.setAttribute('d',
        'M' + (cx - w) + ' ' + top +
        ' L' + (cx + w) + ' ' + top +
        ' L' + (cx + w) + ' ' + shaft +
        ' L' + cx + ' ' + tip +
        ' L' + (cx - w) + ' ' + shaft +
        ' Z');

      /* Fit the frame to the artwork instead of leaving it adrift in a fixed box. A short name
         used to sit small in the middle of a 420x300 field; now the viewBox tracks the actual
         outline, so the topper fills the card whatever the shopper types. */
      var art = unionBox([textEl, stemEl, joinEl]);
      if (art && art.width > 0 && art.height > 0) {
        var pad = Math.max(16, size * 0.24);   // geometry + room for the drop shadow
        svg.setAttribute('viewBox',
          (art.x - pad) + ' ' + (art.y - pad) + ' ' +
          (art.width + pad * 2) + ' ' + (art.height + pad * 2));
      }
      return fits;
    }

    function fit(display) {
      var size = PREF_SIZE, lines = [display], fits = false;
      try {
        /* Prefer ONE line. A name is a name — "Sara Ali" broken across two lines is not what an
           acrylic name topper looks like, and greedy wrapping does exactly that as soon as the
           text is wider than the plate at the preferred size. So: work out what size a single
           line would need, and only wrap when that size drops below what stays legible. Names
           shrink; messages wrap. */
        var words = display.split(/\s+/).filter(Boolean);
        if (!words.length) words = [display];
        var totalW = measure(display, REF_SIZE);
        if (!totalW) return render([display], PREF_SIZE, true);

        /* Try every usable line count and keep the one that renders LARGEST. Shrinking the font
           to force fewer lines was the old approach and it overshot badly: a 48-character
           message ended up at the 22px floor on two lines when three lines would have fitted at
           45px. Width scales linearly with font size, so each candidate is one calculation —
           no iteration, and the answer is the same on every keystroke. */
        var best = null;
        var maxN = Math.min(MAX_LINES, words.length);
        var minN = words.length >= 2 ? 2 : 1;
        for (var n = minN; n <= maxN; n++) {
          /* Split first, then measure THAT split. The old version assumed n equal-width lines,
             which is not what splitInto produces, and it used a fixed line height - so the size
             it picked did not match the block that was actually drawn. */
          var cand = splitInto(words, n, totalW);
          var widestRef = 0;
          for (var i = 0; i < cand.length; i++) {
            var wref = measure(cand[i], REF_SIZE);
            if (wref > widestRef) widestRef = wref;
          }
          if (!widestRef) continue;
          var byW = (SAFE_W / widestRef) * REF_SIZE;
          var byH = SAFE_H / lineOffsets(cand).height;
          var px = Math.min(PREF_SIZE, byW, byH);
          /* Only take an extra row if it buys a genuinely bigger face. */
          if (!best || px > best.size * 1.06) best = { size: px, lines: cand };
        }
        if (!best) return render([display], PREF_SIZE, true);
        lines = best.lines;
        size = best.size;

        /* Budget for the stroke that fuses neighbouring letters. */
        var widest = 0;
        for (var k2 = 0; k2 < lines.length; k2++) {
          var wk = measure(lines[k2], size);
          if (wk > widest) widest = wk;
        }
        var grown = widest + size * JOIN_W;
        if (grown > SAFE_W) size = size * (SAFE_W / grown) * 0.99;

        fits = size >= MIN_SIZE;
        size = Math.max(MIN_SIZE, size);
        return render(lines, size, fits);
      } catch (e) {
        /* getComputedTextLength/getBBox throw when the element has no layout box (card inside a
           closed panel, or fonts not settled). Leave what is there; the fonts.ready and
           ResizeObserver passes re-run this as soon as measuring is possible. */
        return true;
      }
      return fits;
    }

    function limit() {
      var l = (cfg.limits || {});
      return state.script === 'arabic' ? (l.arabic || 50) : (l.latin || 50);
    }

    function validate(raw) {
      if (hasBadChar(raw)) return (cfg.copy && cfg.copy.invalid) || 'Invalid characters';
      if (raw.trim().length > limit()) return (cfg.copy && cfg.copy.tooLong) || 'Too long';
      return '';
    }

    /* ---- Paint: preview + copy. Runs on every keystroke, so it does no work it need not. ---- */
    function paint() {
      var raw = input.value || '';
      var shown = raw.trim();
      var hasName = shown.length > 0;
      /* §14: whitespace is not personalisation — fall back to the placeholder, but never
         rewrite what the shopper typed. */
      var display = hasName ? raw : ((cfg.copy && cfg.copy.placeholder) || 'Custom Name');

      state.text = shown;
      state.script = scriptOf(display);

      /* maxlength follows the script so the two alphabets can have different manufacturable
         limits. Existing over-length text is flagged, not silently truncated (§44). */
      input.setAttribute('maxlength', String(limit()));

      var def = MATERIALS[state.material.key] || MATERIALS.default;
      for (var si = 0; si < stops.length; si++) {
        if (stops[si]) stops[si].setAttribute('stop-color', def.stops[si] || def.stops[def.stops.length - 1]);
      }
      textEl.setAttribute('stroke', def.edge);
      svg.style.setProperty('--tpr-shadow', def.shadow);

      textEl.style.fontFamily = state.script === 'arabic'
        ? ((cfg.fonts && cfg.fonts.arabic) || FONT_ARABIC)
        : ((cfg.fonts && cfg.fonts.latin) || FONT_LATIN);
      /* Native bidi. The string is handed to the browser exactly as typed — never reversed,
         never transliterated — and `plaintext` lets a mixed Latin/Arabic value resolve each
         run on its own first-strong character. */
      textEl.style.direction = state.script === 'arabic' ? 'rtl' : 'ltr';
      textEl.style.unicodeBidi = 'plaintext';
      root.classList.toggle('is-placeholder', !hasName);

      /* fit() owns the text now: it wraps, sizes, lays the tspans out and repositions the stem. */
      var fits = fit(display);
      var msg = validate(raw);
      if (!msg && !fits) msg = (cfg.copy && cfg.copy.tooLong) || 'Too long';
      state.valid = !msg;
      state.error = msg;

      if (errBox) {
        errBox.textContent = msg;
        errBox.hidden = !msg;
        errBox.classList.toggle('is-on', !!msg);
      }
      input.setAttribute('aria-invalid', msg ? 'true' : 'false');

      var c = cfg.copy || {};
      if (eyebrow) eyebrow.textContent = hasName ? (c.eyebrowNamed || '') : (c.eyebrowDefault || '');
      /* The blurb and the select label stay PUT. They used to rewrite themselves with whatever had
         been typed - "Made especially for Sara", "Add Sara's topper" - which moved two lines of
         text on every keystroke while the shopper was still typing into the field right between
         them. The preview is where their name belongs; the copy around it should hold still. */
      /* The price element is the list's running TOTAL now, so paint() must not touch it. What one
         topper costs belongs beside the Add button, where the decision is being made. */
      paintAddPrice();
      /* The heading names the current choice, so the answer to "which colour is this?" is a word
         and not an inference from which chip has a ring round it. */
      if (matLabelEl) matLabelEl.textContent = state.material.label || '';

      chips.forEach(function (c2) {
        var on = c2.m.variantId === state.material.variantId;
        c2.el.classList.toggle('is-on', on);
        c2.el.setAttribute('aria-checked', String(on));
        c2.el.setAttribute('tabindex', on ? '0' : '-1');
      });
    }

    /* ---- Events ---- */
    /* The input event, no debounce and no timer: the preview is local work only. This is also the
       only thing that keeps state.text in step with the field, so everything downstream - Add, the
       validity message, the photograph swap - depends on it existing. */
    /* What we last mirrored in from the recipient name, or null once the box is theirs. The
       mirror writes with .value, which fires no event, so anything this listener hears is the
       shopper typing - and that makes the text theirs from then on. */
    var mirroredText = null;
    input.addEventListener('input', function () {
      mirroredText = null;
      paint();
      if (!input.dataset.started) {
        input.dataset.started = '1';
        emit('topper_personalisation_started', { product_id: cfg.productId });
      }
    });

    /* ---- The list is the cart ----
       Each entry is rendered as its own [data-addon] element carrying is-added, so cart.js sweeps
       it exactly as it sweeps a teddy bear or a box of chocolates, and product.js prices it into
       the Add to cart total. Two names therefore arrive as two lines with two Personalisation
       properties, rather than one line the workshop has to read twice.

       Nothing about either of those files had to change to allow it. The tile contract was always
       per-element; the card simply used to render one element and now renders several. */
    var MIN_QTY = 1, MAX_QTY = 20;

    function lineCents() {
      return state.items.reduce(function (n, it) { return n + (it.material.cents || 0) * it.qty; }, 0);
    }

    /* Built as an element, never as markup in a string: this sits next to text the shopper typed,
       and the rule in this file is that nothing from that direction is ever parsed as HTML. */
    function freePill() {
      var b = document.createElement('b');
      b.className = 'freetag';
      b.textContent = (cfg.copy && cfg.copy.freeTag) || 'FREE';
      return b;
    }

    function totalQty() {
      return state.items.reduce(function (t, it) { return t + it.qty; }, 0);
    }

    /* One topper is already paid for inside the product's own price, so the card must not ask for
       it a second time. The unit taken off is the dearest in the list, which is the unit the
       cart's Buy X Get Y discount gives away too - the two totals have to agree or the shopper
       sees one number here and a different one at checkout. */
    function freeCents() {
      if (!cfg.firstFree || !state.items.length) return 0;
      return state.items.reduce(function (m, it) {
        var c = it.material.cents || 0;
        return c > m ? c : m;
      }, 0);
    }

    function netCents() {
      var n = lineCents() - freeCents();
      return n > 0 ? n : 0;
    }

    /* The button says what pressing it will cost. While the included one is still unclaimed that
       is nothing, so it says so rather than quoting a price that will not be charged. */
    function paintAddPrice() {
      if (!addPriceEl) return;
      var free = !!(cfg.firstFree && totalQty() === 0);
      addPriceEl.textContent = '';
      if (free) addPriceEl.appendChild(freePill());
      else addPriceEl.textContent = state.material.price || '';
      addPriceEl.classList.toggle('is-free', free);
    }

    /* A row the shopper can read, wrapped around attributes the cart can read. Built with
       createElement and textContent throughout: `text` is whatever they typed, and it is going
       back onto the page. */
    function rowFor(it) {
      var li = document.createElement('li');
      /* is-selected, not is-added: Affy's cart.js sweeps [data-addon].is-selected inside the form
         when it submits, and stands those cards down again afterwards. */
      li.className = 'topper__item is-selected';
      li.setAttribute('data-addon', '');
      li.setAttribute('data-topper-item', it.id);
      li.setAttribute('data-addon-id', String(it.material.variantId));
      li.setAttribute('data-addon-cents', String(it.material.cents || 0));
      li.setAttribute('data-addon-qty', String(it.qty));
      li.setAttribute('data-addon-text', it.text);
      /* The cart cannot vary a line's picture by property - Shopify shows the VARIANT's image,
         and a variant here is a MATERIAL, so every gold line would be the gold placeholder
         whatever it says. So the line carries its own: for a ready-made design, the photograph of
         that design in that material. Underscore-prefixed, so it stays out of the customer-facing
         property list by this theme's convention, and the cart renderers prefer it when present.
         A typed topper has no photograph and falls back to the variant image, which is now the
         placeholder in the right colour. */
      /* Personalisation rides in the props here. On the other two shops the wording travels in
         data-addon-text and their cart.js promotes it to a property; Affy reads properties ONLY
         from this JSON, so the words have to be in it or the workshop gets a blank topper. */
      var props = {
        'Personalisation': it.text,
        'Material': it.material.label,
        '_topper_script': it.script,
        '_topper_scene': cfg.scene || 'default',
        '_topper_version': VERSION
      };
      /* Physical cut size, for the workshop. Underscore-prefixed so it stays off the product page
         and out of the customer's cart and confirmation by this theme's convention, while still
         reading on the admin order beside the wording it has to be cut at. Omitted rather than
         left blank if an older cached config carries no size. */
      if (cfg.size) props._topper_size = cfg.size;
      var shelf = (cfg.presetImages || {})[it.text];
      var shot = shelf && shelf[it.material.key];
      if (shot) props._topper_img = shot;
      li.setAttribute('data-addon-props', JSON.stringify(props));

      var what = document.createElement('span');
      what.className = 'topper__itemtext';
      what.setAttribute('dir', 'auto');
      what.textContent = it.text;

      var mat = document.createElement('span');
      mat.className = 'topper__itemmat';
      mat.textContent = it.material.label;

      var qty = document.createElement('span');
      qty.className = 'topper__qty';
      qty.setAttribute('role', 'group');
      /* Named for what it counts, so a screen reader hears "Mariam, quantity" and not six
         identical "quantity" groups down the list. */
      qty.setAttribute('aria-label', it.text);
      var dec = document.createElement('button');
      dec.type = 'button'; dec.className = 'topper__qtybtn';
      dec.setAttribute('data-topper-qty-dec', it.id);
      dec.setAttribute('aria-label', (cfg.copy && cfg.copy.decrease) || '-');
      dec.textContent = '\u2212';
      dec.disabled = it.qty <= MIN_QTY;
      var out = document.createElement('output');
      out.className = 'topper__qtyval';
      out.textContent = String(it.qty);
      var inc = document.createElement('button');
      inc.type = 'button'; inc.className = 'topper__qtybtn';
      inc.setAttribute('data-topper-qty-inc', it.id);
      inc.setAttribute('aria-label', (cfg.copy && cfg.copy.increase) || '+');
      inc.textContent = '+';
      inc.disabled = it.qty >= MAX_QTY;
      qty.appendChild(dec); qty.appendChild(out); qty.appendChild(inc);

      var rm = document.createElement('button');
      rm.type = 'button'; rm.className = 'topper__itemx';
      rm.setAttribute('data-topper-remove', it.id);
      /* The accessible name carries the text, or every remove button on the list reads the same. */
      rm.setAttribute('aria-label', ((cfg.copy && cfg.copy.remove) || 'Remove') + ': ' + it.text);
      rm.textContent = '\u00d7';

      li.appendChild(what); li.appendChild(mat); li.appendChild(qty); li.appendChild(rm);
      return li;
    }

    function renderList() {
      listEl.textContent = '';
      state.items.forEach(function (it) { listEl.appendChild(rowFor(it)); });
      if (chosenEl) chosenEl.hidden = state.items.length === 0;
      root.classList.toggle('is-chosen', state.items.length > 0);
      sumUp();
      renderPresets();   // the tiles mirror the list, so they repaint with it
    }

    /* The footer is the CARD's total, so it counts the preset tiles as well as the typed rows -
       they are equally in the cart, they just live in different places on the page. The list above
       it stays typed-only, because a preset already says what it is where it sits. */
    function sumUp() {
      var n = totalQty();
      if (countEl) {
        countEl.textContent = n ? String((cfg.copy && cfg.copy.totalCount) || '{{ count }}')
          .replace('{{ count }}', String(n)).replace('{{count}}', String(n)) : '';
      }
      if (freeEl) {
        var showFree = !!(cfg.firstFree && n > 0);
        freeEl.textContent = '';
        if (showFree) {
          freeEl.appendChild(freePill());
          freeEl.appendChild(document.createTextNode(' ' + ((cfg.copy && cfg.copy.includedCount) || '')));
        }
        freeEl.hidden = !showFree;
      }
      if (priceEl) {
        var net = netCents();
        priceEl.textContent = n && net > 0 ? '+ ' + money(net) : '';
      }
      paintAddPrice();
      /* product.js paints the Add to cart running total from the add-on tiles and knows
         nothing about the included one, so the card states what it is giving away and lets
         that total subtract it. */
      root.setAttribute('data-topper-free-cents', String(freeCents()));
      if (sumEl) sumEl.hidden = n === 0;
      /* The prompt asked for a topper and one has arrived, so stop asking. Leaving it up made
         the card argue with itself: "add your free topper" sitting above the topper just added. */
      if (nudgeEl && n > 0) { nudgeEl.hidden = true; setBuyWaiting(false); }
      /* product.js reprices the Add to cart button from a `change` on the form. */
      try { (form || root).dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
    }

    /* Formats from the cents of the materials themselves rather than a money filter this file does
       not have. Every material on this product is the same currency, so the only thing needed is
       the pattern of the one price Liquid already rendered - "AED 55.00" gives prefix and
       separator, and the number is substituted into it. Falls back to plain cents if that price
       string is not parseable, which is better than printing a wrong currency. */
    function money(cents) {
      var sample = (state.material && state.material.price) || '';
      var m = sample.match(/^(\D*)([\d.,]+)(\D*)$/);
      var n = (cents / 100).toFixed(2);
      return m ? m[1] + n + m[3] : n;
    }

    /* Pressing Add with nothing typed, or with something the cutter cannot render, should say why
       rather than doing nothing. Nothing is ever added silently and nothing is ever refused
       silently either. */
    function nudgeForText() {
      paint();
      if (!state.text && errBox) {
        errBox.textContent = (cfg.copy && cfg.copy.blurbDefault) || '';
        errBox.hidden = false; errBox.classList.add('is-on');
      }
      input.focus();
    }

    function findSame(text, material) {
      for (var i = 0; i < state.items.length; i++) {
        if (state.items[i].text === text && state.items[i].material.variantId === material.variantId) return state.items[i];
      }
      return null;
    }

    /* Adding the same wording in the same material twice is almost always "I want two of these",
       not "I want two identical rows to manage separately". */
    /* ---- The press has to answer for itself ----
       The ADDED list is the real record, but it renders further down the card - below the fold on
       a phone - so a press that empties the box and otherwise changes nothing visible reads as a
       button that did nothing. Tick and word, on the button, gone again in under two seconds. */
    var addedTimer = null;
    function clearAddedCue() {
      if (addedTimer) { clearTimeout(addedTimer); addedTimer = null; }
      addBtn.classList.remove('is-added');
      if (addTickEl) addTickEl.hidden = true;
      if (addLabelEl) addLabelEl.textContent = addLabelDefault;
      if (liveEl) liveEl.textContent = '';
    }
    function showAddedCue() {
      var word = (cfg.copy && cfg.copy.added) || addLabelDefault;
      if (addTickEl) addTickEl.hidden = false;
      if (addLabelEl) addLabelEl.textContent = word;
      addBtn.classList.add('is-added');
      /* Announced separately: a label that changes under the pointer is not reliably read out, and
         role="status" speaks without stealing focus from the field they are about to type in. */
      if (liveEl) liveEl.textContent = word;
      addedTimer = setTimeout(clearAddedCue, 1800);
    }

    function addCurrent() {
      clearAddedCue();          // this press answers for itself; it never re-reads the last one
      if (!state.text || !state.valid) { nudgeForText(); return; }
      /* Only claim success if something actually joined the list. addText refuses a blank or a
         sold-out material, and a button that says Added over a refusal is worse than silence. */
      var before = totalQty();
      addText(state.text);
      if (totalQty() === before) return;
      /* Clear for the next one. The preview returns to the placeholder, which is the signal that
         the thing just built is now in the list rather than still under the cursor. */
      input.value = '';
      delete input.dataset.started;
      paint();
      showAddedCue();
      input.focus();
    }

    /* The one place a topper joins the list, whether it was typed or tapped from a preset. */
    function addText(text) {
      var words = String(text || '').trim();
      if (!words) { nudgeForText(); return; }
      if (!state.material.available) { nudgeForText(); return; }
      var dup = findSame(words, state.material);
      if (dup) {
        dup.qty = Math.min(MAX_QTY, dup.qty + 1);
      } else {
        state.seq += 1;
        state.items.push({ id: 't' + state.seq, text: words, script: scriptOf(words),
                           material: state.material, qty: MIN_QTY });
      }
      renderList();
      emit('topper_added', {
        product_id: cfg.productId,
        topper_variant_id: state.material.variantId,
        material: state.material.key,
        script: state.script,
        text_length: words.length,
        items: state.items.length,
        value: (state.material.cents || 0) / 100
      });
    }

    function removeItem(id) {
      var before = state.items.length;
      state.items = state.items.filter(function (it) { return it.id !== id; });
      if (state.items.length !== before) {
        renderList();
        emit('topper_removed', { product_id: cfg.productId, items: state.items.length });
      }
    }

    function setItemQty(id, n) {
      state.items.forEach(function (it) {
        if (it.id !== id) return;
        var next = Math.max(MIN_QTY, Math.min(MAX_QTY, n));
        if (next === it.qty) return;
        it.qty = next;
        emit('topper_quantity_changed', { product_id: cfg.productId, quantity: next });
      });
      renderList();
    }

    /* One delegated listener rather than one per row: rows are rebuilt on every change, and
       per-row listeners would have to be rebuilt with them. */
    listEl.addEventListener('click', function (e) {
      var t = e.target.closest('[data-topper-remove],[data-topper-qty-dec],[data-topper-qty-inc]');
      if (!t) return;
      e.preventDefault();
      var id = t.getAttribute('data-topper-remove') || t.getAttribute('data-topper-qty-dec') ||
               t.getAttribute('data-topper-qty-inc');
      var item = state.items.filter(function (it) { return it.id === id; })[0];
      if (t.hasAttribute('data-topper-remove')) { removeItem(id); return; }
      if (!item) return;
      setItemQty(id, item.qty + (t.hasAttribute('data-topper-qty-inc') ? 1 : -1));
    });

    /* One topper is already paid for inside the product's price, so someone pressing Add to cart
       without one is about to walk away from something they have bought. Ask - once, where they
       are already looking, and never in a way that can stop them: the flag is set BEFORE the
       prompt is shown, so a second press always goes straight through.

       Capture phase on purpose. Both themes listen for submit in the bubble phase (one on the
       form, one delegated on document), so a capture listener here is the only place that can
       hold the submit before either of them starts building a cart request.

       Two things are deliberately left alone. Buy now keeps its own submitter and its own path -
       a checkout is not the place to experiment. And an invalid form is left to the theme's own
       validation, so a missing delivery date is reported before an upsell is offered, not after. */
    /* The prompt belongs beside the buy buttons, and where those sit relative to the <form> is the
       theme's business: on tnegifts and bloomistry they are inside it, on affy the buy row is
       outside and this lookup came back null - so the prompt existed in the page and nothing ever
       showed it. Form first, so a page with more than one product form still pairs correctly; the
       document is the fallback. */
    var nudgeEl = (form && form.querySelector('[data-topper-nudge]')) || document.querySelector('[data-topper-nudge]');
    var declined = false;   // set by "no thanks", and spent by the very next submit

    /* While the question is open, the buy buttons are visibly not the way forward. They stay
       clickable on purpose - a truly disabled button gives a confused shopper no feedback at
       all - but they are dimmed and marked aria-disabled, and pressing one flashes the prompt. */
    function setBuyWaiting(on) {
      /* form.elements, not a data-attribute hunt. The sticky bar's Add to cart carries neither
         data-atc nor data-buy-now and lives OUTSIDE the form, joining it by form="product-form" -
         so the old selector missed it and the floating bar on a phone stayed fully lit while
         every other buy button dimmed. form.elements includes form-ASSOCIATED controls wherever
         they sit in the DOM, which is exactly the set that can submit this form. */
      var btns = [];
      var els = (form && form.elements) || [];
      for (var k = 0; k < els.length; k++) {
        if (els[k] && els[k].type === 'submit') btns.push(els[k]);
      }
      for (var i = 0; i < btns.length; i++) {
        btns[i].classList.toggle('is-awaiting-answer', !!on);
        if (on) btns[i].setAttribute('aria-disabled', 'true');
        else btns[i].removeAttribute('aria-disabled');
      }
      if (!on && nudgeEl) nudgeEl.classList.remove('is-asking');
    }

    var pendingSubmitter = null;

    if (form && nudgeEl) {
      form.addEventListener('submit', function (e) {
        /* The offer has to be ANSWERED, not merely shown. The first version set this flag before
           painting the prompt, so a second press - a double-tap on a phone - went straight
           through and the shopper never read the thing they had already paid for. Now only an
           explicit "no thanks" sets it, so the prompt returns until a choice is made. Declining
           is still one tap and is never refused. */
        if (!cfg.firstFree || totalQty() > 0) return;
        if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
        /* A decline covers the ONE add it was asked for, then it is spent. It used to last the
           whole page: decline once, empty the cart, come back and press Add to cart, and the
           offer never appeared again. Consuming it here also matches the discount, which is per
           product - a second gift earns a second free topper, so it deserves to be asked about. */
        if (declined) { declined = false; return; }
        /* Buy now is held too - it skips the cart entirely, so it is the LAST place someone
           should be able to walk past something they have already paid for. The button that was
           pressed is remembered because both themes decide express-vs-cart from e.submitter, and
           a resubmit without it would quietly turn Buy now into Add to cart. */
        pendingSubmitter = e.submitter || null;
        e.preventDefault();
        e.stopPropagation();
        /* A press that does nothing reads as a broken button. If the prompt is already up, the
           shopper has pressed past it - so say so: pull focus back and flash it, rather than
           swallowing the press in silence. */
        nudgeEl.hidden = false;
        setBuyWaiting(true);
        /* Fire the cue on EVERY held press, including the first. It used to fire only on a
           repeat press, so pressing once gave a silent appearance and pressing twice gave a
           shake - two different experiences from the same action, which is indistinguishable
           from one store being broken. */
        nudgeEl.classList.remove('is-asking');
        void nudgeEl.offsetWidth;          // reflow, so the animation restarts every time
        nudgeEl.classList.add('is-asking');
        try { nudgeEl.focus(); } catch (err) {}
      }, true);

      var nYes = nudgeEl.querySelector('[data-topper-nudge-yes]');
      var nNo  = nudgeEl.querySelector('[data-topper-nudge-no]');

      if (nYes) nYes.addEventListener('click', function () {
        nudgeEl.hidden = true;
        setBuyWaiting(false);
        try { root.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (err) {}
        try { input.focus({ preventScroll: true }); } catch (err) { input.focus(); }
      });

      if (nNo) nNo.addEventListener('click', function () {
        declined = true;              // the one thing that lets the order through without a topper
        nudgeEl.hidden = true;
        setBuyWaiting(false);
        var by = pendingSubmitter;
        pendingSubmitter = null;
        /* Hand the original button back so Buy now stays Buy now. requestSubmit throws if the
           button is not associated with this form - the sticky bar's lives outside it and joins
           by form="product-form" - so clicking it is the fallback, and `declined` is already set
           so that press goes straight through. */
        try {
          if (form.requestSubmit) { form.requestSubmit(by || undefined); return; }
        } catch (err) {}
        if (by && by.click) { by.click(); return; }
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });

      nudgeEl.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        nudgeEl.hidden = true;
        var atc = form.querySelector('[data-atc]');
        if (atc) try { atc.focus(); } catch (err) {}
      });
    }

    addBtn.addEventListener('click', function () { addCurrent(); });
    /* Enter in the field means the same thing as pressing Add, and stops the keypress reaching
       the product form, where it would submit the whole order instead. */
    input.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      addCurrent();
    });

    /* Kept as a no-op name so the rest of the file reads unchanged; the list is the cart now. */
    function sync() { renderList(); }

    /* Reuse the recipient name the shopper has already given (§16) — but only while they have
       not typed a topper of their own, so mirroring never overwrites a deliberate choice. */
    /* The test is the BOX, not a flag. `started` is set by the first keystroke and never
       cleared, so typing one letter here and deleting it used to kill the mirror for the rest of
       the visit - and the prompt's "Add my FREE topper" puts focus in this box, which makes that
       a normal thing to do. An empty box holds no choice to protect.

       Delegated from the document instead of bound to the node found at build time: Shopify
       re-renders this section (theme editor, section swap) and a bound listener dies silently
       with the node it was attached to. Matched on the property name as well as the id, so
       renaming the field in the markup cannot quietly unhook it. And `change` as well as
       `input`, because autofill and password managers fill a field without typing in it. */
    var RNAME_SEL = '[data-gift-recipient],[name="properties[Recipient name]"]';
    function mirrorRecipient(el) {
      if (!el) return;
      /* Ours to write while the box is empty or still holds exactly what we last put there.
         `started` cannot answer this: it is set by the first keystroke and never cleared, so it
         called a box the shopper had since emptied - and a box we had just filled ourselves -
         somebody's deliberate choice. */
      if (input.value !== '' && input.value !== mirroredText) return;
      input.value = el.value;
      mirroredText = el.value;
      paint();
    }
    ['input', 'change'].forEach(function (ev) {
      document.addEventListener(ev, function (e) {
        var t = e.target;
        if (t && t.matches && t.matches(RNAME_SEL)) mirrorRecipient(t);
      }, true);
    });
    /* A name already in the field when the card initialises - autofill, a restored form, or a
       section re-render after it was typed - never fired an event we could hear. */
    var rnameNow = document.querySelector(RNAME_SEL);
    if (rnameNow && rnameNow.value) mirrorRecipient(rnameNow);

    try {
      /* After a successful add Affy's cart.js clears .is-selected from every [data-addon] in the
         form, ours included. That is the signal the order went through, and the list has to empty
         with it - otherwise the next add repeats every topper already bought. Watching the rows
         rather than a flag: they ARE the state now. */
      new MutationObserver(function () {
        if (!state.items.length) return;
        var live = listEl.querySelectorAll('.topper__item.is-selected').length;
        if (live === state.items.length) return;
        state.items = [];
        input.value = '';
        delete input.dataset.started;
        paint();
        renderPresets();
        renderList();
      }).observe(listEl, { attributes: true, subtree: true, attributeFilter: ['class'] });
    } catch (e) {}

    /* Re-measure when the things that change measurement change: fonts arriving, and the card
       being resized (rotation, drawer open, breakpoint). */
    try {
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { paint(); });
    } catch (e) {}
    try {
      if (window.ResizeObserver) new ResizeObserver(function () { paint(); }).observe(stage);
    } catch (e) {}

    /* Suggested phrases: fill the box and redraw, exactly as if it had been typed. Deliberately
       does NOT tick the checkbox - picking a wording is not the same as deciding to buy. */
    root.querySelectorAll('[data-topper-suggest]').forEach(function (b) {
      b.addEventListener('click', function () {
        input.value = b.getAttribute('data-topper-suggest') || '';
        input.dataset.started = '1';          // stop the recipient field overwriting a deliberate pick
        paint();
        input.focus();
        emit('topper_suggestion_used', { product_id: cfg.productId, text_length: state.text.length });
      });
    });

    /* The preview is the RENDER of whatever is in the field, always. It used to swap to a
       photograph when the text matched a ready-made phrase, which made sense when those
       photographs had nowhere else to be - they have their own tiles below now, and a preview that
       sometimes shows a render and sometimes a photograph is a preview you cannot trust to be
       showing your words. */

    /* ---- Presets ----
       A photographed design is sold the way every other ready-made add-on in this theme is sold:
       a tile you tick, carrying its own count. The tile IS the [data-addon] element, so cart.js
       sweeps it with everything else and each chosen design becomes its own line. It never joins
       the ADDED list -- the list is for typed toppers, which have no tile of their own to live on.

       MATERIAL. Every photograph is gold, but the colour is not what is preset: the shape is. A
       preset records the material that was chosen at the moment it was ticked, exactly as a typed
       topper records it at the moment it is added, and the tile then says so. Changing material
       afterwards affects the next thing chosen, never something already decided - the same rule in
       both halves of the card, and the only one that does not rewrite an order behind the
       shopper's back. */
    var presetEls = {};
    root.querySelectorAll('[data-topper-preset]').forEach(function (el) {
      presetEls[el.getAttribute('data-topper-preset')] = el;
    });

    /* The row for this design in the colour currently chosen - which is the only row a tile ever
       speaks for. Tick it in gold, switch to silver, and the tile reads empty again: the gold one
       is still in the list below, and ticking now makes a second row rather than recolouring the
       first. That is how one gold and one silver get ordered together. */
    function presetItem(k) { return findSame(k, state.material); }

    function renderPresets() {
      Object.keys(presetEls).forEach(function (el_k) {
        var el = presetEls[el_k];
        var it = presetItem(el_k);
        var qty = it ? it.qty : 0;
        var on = qty > 0;

        el.classList.toggle('is-on', on);
        var box = el.querySelector('[data-topper-preset-check]');
        if (box) box.checked = on;
        var out = el.querySelector('[data-topper-pval]');
        if (out) out.textContent = String(qty);
        var dec = el.querySelector('[data-topper-pdec]');
        if (dec) dec.disabled = qty <= 0;
        var inc = el.querySelector('[data-topper-pinc]');
        if (inc) inc.disabled = qty >= MAX_QTY;

        /* Always the colour chosen right now. The tile is a preview of what ticking it would
           give you, not a record of anything, so it is free to follow the chips. */
        var img = el.querySelector('.topper__presetimg');
        var shelf = (cfg.presetImages || {})[el_k];
        if (img && shelf) {
          var src = shelf[state.material.key];
          if (src && img.getAttribute('src') !== src) img.setAttribute('src', src);
        }
      });
    }

    function setPresetQty(k, n) {
      if (!presetEls[k]) return;
      var it = presetItem(k);
      var next = Math.max(0, Math.min(MAX_QTY, n));
      if (next === 0) {
        if (it) removeItem(it.id);
        else renderPresets();
        return;
      }
      if (it) { setItemQty(it.id, next); return; }
      addText(k);                       // first one: joins the list like anything else
      var made = presetItem(k);
      if (made && next > 1) setItemQty(made.id, next);
      else renderPresets();
    }

    root.querySelectorAll('[data-topper-preset-check]').forEach(function (box) {
      box.addEventListener('change', function () {
        var k = box.getAttribute('data-topper-preset-check');
        setPresetQty(k, box.checked ? MIN_QTY : 0);
      });
    });
    root.querySelectorAll('[data-topper-preset]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        var t = e.target.closest('[data-topper-pinc],[data-topper-pdec]');
        if (!t) return;
        e.preventDefault();
        var inc = t.hasAttribute('data-topper-pinc');
        var k = t.getAttribute(inc ? 'data-topper-pinc' : 'data-topper-pdec');
        var it = presetItem(k);
        setPresetQty(k, (it ? it.qty : 0) + (inc ? 1 : -1));
      });
    });

    paint();
    renderList();
    emit('topper_impression', { product_id: cfg.productId, scene: cfg.scene });
    return true;
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-topper]');
    for (var i = 0; i < nodes.length; i++) {
      var root = nodes[i];
      if (root.dataset.topperReady === '1') continue;   // survives section re-render (§67)
      root.dataset.topperReady = '1';
      try {
        var cfg = parseConfig(root);
        if (!cfg) { root.hidden = true; continue; }
        if (!build(root, cfg)) root.hidden = true;
      } catch (e) {
        root.hidden = true;                             // §54: never take the page down with us
        if (window.console) console.error('[topper] init failed:', e);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  /* Shopify replaces section DOM in the theme editor and on some variant changes. */
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:select', initAll);
})();
